import { useContext, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Budget from "./Budget";
import CurrentScore from "./CurrentScore";
import CurrentTime from "./Time/CurrentTime";
import Events from "./Event";
import Room from "./Room";
import Tasks from "./Tasks";
import Weather from "./Weather";
import ConsultMeteo from "./Weather/ConsultMeteo";
import AdvanceTime from "./Time/AdvanceTime";
import Controller from "./Controller";
import { BEGIN_TIME, DEFAULT_AVAILABLE_POINTS, DEFAULT_DATA, SLEEP_TIME, TICK, TICK_DURATION, TRANSITION_DURATION } from "../../../constants/constants";
import { addMinutes, displayTime, getMinutesInBetween, isBefore, TimeInterface } from "../../../interface/Time";
import { getTemperatures, HourlyWeather } from "./Weather/WeatherUtils";
import Round from "./Round";
import { SpontaneousEvent } from "../../../interface/CustomEvent";
import { useModal } from "../../../hooks/useModal";
import { Popup } from "../../Popup/Popup";
import SpontaneousEventPopup from "./Event/SpontaneousEventPopup";
import { Box, Typography, Divider, useMediaQuery } from "@mui/material";
import { Task, TaskCompletion, TaskStatus } from "../../../interface/Task";
import { GameContext, IGameContext } from "../../../contexts/GameContext";
import { Data } from "../../../interface/Data";
import { AppContext, IAppContext } from "../../../contexts/AppContext";
import IconArrowDown from "./IconArrowDown";
import IconArrowUp from "./IconArrowUp";
import InfoIconCarre from "./Card/iconInfo";
import { ResultsService } from "../../../services/results/ResultsService"


interface Props {
    onEndRound: () => void
}


function getNSteps(begin: TimeInterface, end: TimeInterface, dur: number) {
    return getMinutesInBetween(begin, end) / dur;
}


const GameInterface = ({ onEndRound }: Props) => {
    const { round, data, values, setValues, setData, scores, setScores, results, setResults, maxPoints, maxRound, time, setTime,
        points, setPoints, events, setEvents, spontaneousEvents, setSpontaneousEvents, nPeople, finalScores, gameState,
        tasks, setTasks, setTempIn, currentStep, setCurrentStep, endTime, beginTime, stepDuration, gameId } = useContext<IGameContext>(GameContext);

    const { jwtToken, server, socket, userId } = useContext<IAppContext>(AppContext);


    const [targetTime, setTargetTime] = useState<TimeInterface>(addMinutes(time, 60)); // Time to move to.
    const [nextHour, setNextHour] = useState<TimeInterface>(addMinutes(BEGIN_TIME, 60)); // Time value of the following hour (e.g. 8:00 if it is 7:20).

    const [weatherPrevisions, setWeatherPrevisions] = useState<HourlyWeather[]>(getTemperatures(time.hours)); // Weather value of the upcoming 24 hours.

    const [availablePoints, setAvailablePoints] = useState(maxPoints[round]); // Preview of the number of points.

    const [targetData, setTargetData] = useState<Data>(DEFAULT_DATA);   // Modified stats of parameter inputs.

    const [canTrigger, setTrigger] = useState<boolean>(true);   // Indicates if transitions can be applied.

    const [timeoutIds, setTimeoutIds] = useState<NodeJS.Timeout[]>([]);

    const { isShown: showEventPopup, toggle: toggleEventPopup } = useModal();
    const [targetEvent, setTargetEvent] = useState<SpontaneousEvent | null>(null);
    const [targetEventId, setTargetEventId] = useState<number>(0);

    const [tasksInProgress, setTasksInProgress] = useState<TaskCompletion[]>([]);

    const [simulationResults, setSimulationResults] = useState<any>(null);

    const [futureTime, setFutureTime] = useState<TimeInterface>(BEGIN_TIME);

    const [electricCosts, setElectricCosts] = useState<number[]>(new Array(getNSteps(beginTime[round], endTime[round], stepDuration)).fill(0));

    const eventsScroller = useRef<null | HTMLDivElement>(null);

    const [scrollToBottom, setScrollToBottom] = useState<boolean>(false);



    const [topPanel, setTopPanel] = useState<{open:boolean }>({open:true});


    useEffect(() => {
        document.body.style.cursor = "auto";
        if (simulationResults !== null && simulationResults.length > 0) {
            console.log(`[Game Interface] ========== SIMULATION RESULTS RECEIVED ==========`);
            console.log(`[Game Interface] Processing simulation results:`, simulationResults.length);
            console.log(`[Game Interface] Current time: ${displayTime(time)}, Future time: ${displayTime(futureTime)}`);
            console.log(`[Game Interface] Simulation data sample:`, simulationResults[0]);
            if (simulationResults[0].step === -1) {
                setTempIn(simulationResults[0].tempIn);
                setValues(simulationResults[0].values);
                const scoresClone = scores.slice();
                const timestamp = displayTime(BEGIN_TIME);
                scoresClone[round].push({ time: timestamp, values: simulationResults[0].values, score: simulationResults[0].score });
                setScores(scoresClone);

                const resultsClone = results.slice();
                resultsClone[round]["timestamps"].push({
                    timestamp: timestamp,
                    data: { ...data, "electric_task": 0 },
                    values: {...simulationResults[0].values, tempIn:simulationResults[0].tempIn},
                    score: simulationResults[0].score
                });
                setResults(resultsClone);
            } else {
                // Before simulation begins
                console.log(`[Game Interface] Processing time advancement simulation`);
                setTrigger(false);
                setData(targetData);

                const oldStep = currentStep;

                if (isBefore(time, SLEEP_TIME)) {
                    const N_MINUTES = getMinutesInBetween(time, futureTime);
                    const N_STEPS = Math.floor(N_MINUTES / TICK)
                    
                    console.log(`[Game Interface] N_MINUTES: ${N_MINUTES}, N_STEPS: ${N_STEPS}`);

                    const initialTime = time;
                    const timeoutIds = [];

                    for (let i = 0; i < N_STEPS; ++i) {
                        const nTicks = Math.min(TICK, TICK_DURATION);

                        for (let j = 1; j <= nTicks; ++j) {
                            // Called at each tick.
                            const timeoutId = setTimeout(() => {
                                setTime(addMinutes(initialTime, Math.floor(i * TICK + j / nTicks * TICK)));
                            }, i * TICK_DURATION + j / nTicks * TICK_DURATION);
                            timeoutIds.push(timeoutId);
                        }
                        // Called at the end of each step.
                        const timeoutId = setTimeout(() => {
                            // New values
                            const stepValues = {...simulationResults[i].values, tempIn:simulationResults[i].tempIn}
                            setValues(stepValues);

                            // New scores
                            const scoresClone = scores.slice();
                            const timestamp = displayTime(addMinutes(time, (i + 1) * TICK));
                            scoresClone[round].push({ time: timestamp, values: stepValues, score: simulationResults[i].score as number });
                            setScores(scoresClone);

                            //New results
                            const resultsClone = results.slice();
                            
                            resultsClone[round]["timestamps"].push({
                                timestamp: timestamp,
                                data: { ...targetData, "electric_task": electricCosts[oldStep + i] },
                                values: stepValues,
                                score: simulationResults[i].score as number
                            });
                            setResults(resultsClone);

                            // New temperature
                            setTempIn(simulationResults[i].tempIn);

                            setCurrentStep(oldStep + i + 1);
                            
                            
                        }, i * TICK_DURATION);
                        timeoutIds.push(timeoutId);

                    }

                    setTimeoutIds(timeoutIds);

                    setTimeout(async () => {
                        for (let i = 0; i < N_STEPS; ++i) {
                            if(jwtToken){
                                const timestamp = displayTime(addMinutes(time, (i + 1) * TICK));
                                const stepValues = simulationResults[i].values
                                const timespampsResult = {
                                        timestamp: timestamp,
                                        data: { ...targetData, "electric_task": electricCosts[oldStep + i] },
                                        values: {...stepValues, tempIn:simulationResults[i].tempIn}, 
                                        score: simulationResults[i].score as number
                                    }
                                await ResultsService.sendProgress(jwtToken,{round:round, result:timespampsResult, score:simulationResults[i].score, id: gameId})
                            }
                        }
                    })
                }

                // At the end of the simulation
                setTimeout(() => {
                    console.log(`[Game Interface] ========== SIMULATION COMPLETE ==========`);
                    console.log(`[Game Interface] Setting time from ${displayTime(time)} to ${displayTime(futureTime)}`);
                    console.log(`[Game Interface] Updating UI and enabling trigger`);
                    
                    // CRITICAL FIX: Update time to futureTime after simulation completes
                    setTime(futureTime);
                    setTrigger(true);
                    document.body.style.cursor = "default";
                    
                    console.log(`[Game Interface] Time advancement complete!`);
                }, simulationResults.length * TICK_DURATION)
            }
        }
    }, [simulationResults]);

    useEffect(() => {
        socket?.bindFn((val: any) => { setSimulationResults(val) });
    }, [round])


    useEffect(() => {
        // Scroll events to bottom.
        if (eventsScroller.current) {
            eventsScroller.current.scrollTop = 1000
        }
    }, [scrollToBottom])

    useEffect(() => {
        if (jwtToken && userId && socket && server && socket.isConnected) {
            socket.bindFn((val: any) => { setSimulationResults(val) });
            // socket.setDisconnectFn(onDisconnect);
            socket.onNewRound({ server: server, round: round }, data);
        }
    }, [socket, socket?.isConnected]);

    // Makes sure targetData is always up to date with current data.
    useEffect(() => {
        setTargetData(data);
        console.log("target", data)
    }, [data])

    // Updates points when new max available points is changed.
    useEffect(() => {
        setAvailablePoints(points);
    }, [points]);

    // When time changes...
    useEffect(() => {
        // Get new weather on new hour.
        if (!isBefore(time, nextHour)) {
            setWeatherPrevisions(getTemperatures(time.hours));
            setNextHour(addMinutes(nextHour, 60));
        }

        // Update target time.
        const newTargetTime = addMinutes(time, 60);
        if (isBefore(SLEEP_TIME, newTargetTime)) {
            setTargetTime(SLEEP_TIME);
        } else {
            setTargetTime(
                {
                    hours: newTargetTime.hours % 24,
                    minutes: newTargetTime.minutes
                }
            );
        }

        // Check if tasks are in progress.
        let electricReduction = 0
        const idxToRemove: number[] = []
        const clonedEvents = events.slice();
        const clonedTasks = tasks.slice();
        const clonedTasksInProg = tasksInProgress.slice();

        tasksInProgress.forEach((obj, idx) => {
            if (!(isBefore(time, obj.time))) {
                idxToRemove.push(idx)
                if (obj.eventId !== undefined && obj.taskId !== undefined) {
                    clonedEvents[round][obj.eventId].tasks[obj.taskId].status = TaskStatus.DONE;
                    electricReduction += clonedEvents[round][obj.eventId].tasks[obj.taskId].electricCost;
                } else if (obj.taskId !== undefined) {
                    clonedTasks[obj.taskId].status = TaskStatus.DONE;
                    electricReduction += clonedTasks[obj.taskId].electricCost;
                }


            }
        });

        const newTasksInProg = clonedTasksInProg.filter((val, idx) => !idxToRemove.includes(idx))

        if (idxToRemove.length > 0) {
            setEvents(clonedEvents);
            setTasks(clonedTasks);
            setTasksInProgress(newTasksInProg);
            setData(prev => ({ ...prev, ["electric_equipement"]: prev["electric_equipement"] - electricReduction }));
        }

    }, [time]);

    useEffect(() => {
        setScrollToBottom(!scrollToBottom);
    }, [targetEvent]);


    function triggerTask(eventId?: number, taskId?: number, spontaneous?: boolean) {
        // Trigger action from event
        if (taskId !== undefined && eventId !== undefined) {
            const clonedEvents = events.slice();
            clonedEvents[round][eventId].tasks[taskId].status = clonedEvents[round][eventId].tasks[taskId].status === TaskStatus.IN_PROGESS ? TaskStatus.AVAILABLE : TaskStatus.IN_PROGESS;
            setEvents(clonedEvents);

            if (clonedEvents[round][eventId].tasks[taskId].status === TaskStatus.IN_PROGESS) {
                setTasksInProgress((prev) => [...prev, {
                    time: addMinutes(time, clonedEvents[round][eventId].tasks[taskId].duration),
                    taskId: taskId,
                    eventId: eventId,
                    canCancel: spontaneous !== true
                }]);
                setAvailablePoints(availablePoints - clonedEvents[round][eventId].tasks[taskId].energyCost)
                setTargetData(prev => ({ ...prev, ["electric_equipement"]: prev["electric_equipement"] + clonedEvents[round][eventId].tasks[taskId].electricCost }));
            } else {
                // Remove task from tasks in progress.
                setTasksInProgress((prev) => prev.filter(element => element.eventId !== eventId || element.taskId !== taskId));
                setAvailablePoints(availablePoints + clonedEvents[round][eventId].tasks[taskId].energyCost)
                setTargetData(prev => ({ ...prev, ["electric_equipement"]: prev["electric_equipement"] - clonedEvents[round][eventId].tasks[taskId].electricCost }));
            }

            // Trigger action from daily task
        } else if (taskId !== undefined) {
            const clonedTasks = tasks.slice();
            clonedTasks[taskId].status = clonedTasks[taskId].status === TaskStatus.IN_PROGESS ? TaskStatus.AVAILABLE : TaskStatus.IN_PROGESS;
            setTasks(clonedTasks);

            if (clonedTasks[taskId].status === TaskStatus.IN_PROGESS) {
                setTasksInProgress((prev) => [...prev, {
                    time: addMinutes(time, clonedTasks[taskId].duration),
                    taskId: taskId,
                    canCancel: true
                }]);
                setAvailablePoints(availablePoints - clonedTasks[taskId].energyCost);
                setTargetData(prev => ({ ...prev, ["electric_equipement"]: prev["electric_equipement"] + clonedTasks[taskId].electricCost }));
            } else {
                setTasksInProgress((prev) => prev.filter(element => element.eventId !== eventId || element.taskId !== taskId));
                setAvailablePoints(availablePoints + clonedTasks[taskId].energyCost);
                setTargetData(prev => ({ ...prev, ["electric_equipement"]: prev["electric_equipement"] - clonedTasks[taskId].electricCost }));
            }
        }
    }


    function triggerAction(nextTime: TimeInterface, newPoints?: number, eventId?: number, taskId?: number) {
        console.log(`[Game Interface] triggerAction called`);
        console.log(`[Game Interface] Current time: ${displayTime(time)}, Target time: ${displayTime(nextTime)}`);
        console.log(`[Game Interface] New points: ${newPoints}, Available points: ${availablePoints}`);
        console.log(`[Game Interface] EventId: ${eventId}, TaskId: ${taskId}`);
        
        // Add electric costs, if any.
        const duration = getMinutesInBetween(time, nextTime);
        const stepD = duration / stepDuration;
        console.log(`[Game Interface] Duration: ${duration} minutes, Steps: ${stepD}`);
        
        const electricCostsClone = electricCosts.slice();
        if (taskId !== undefined && eventId !== undefined) {
            const s = currentStep;
            const e = currentStep + stepD
            console.log(`[Game Interface] Adding electric costs from step ${s} to ${e}`);
            for (let i = s; i < e; ++i) {
                electricCostsClone[i] += events[round][eventId].tasks[taskId].electricCost / stepD
            }
        }
        setElectricCosts(electricCostsClone);

        // Look for any spontaneous event that might occur.
        let newTargetTime = nextTime;


        // Limit to end time.
        if (isBefore(endTime[round], newTargetTime)) {
            newTargetTime = endTime[round];
        }

        console.log(`[Game Interface] Final target time: ${displayTime(newTargetTime)}`);

        const dataList = [];
        const nSteps = getMinutesInBetween(time, newTargetTime) / stepDuration;
        setFutureTime(newTargetTime);

        console.log(`[Game Interface] Creating ${nSteps} simulation steps`);
        for (let i = 0; i < nSteps; ++i) { 
            const stepData = { data: { "electric_task": electricCostsClone[i + currentStep], ...targetData } };
            dataList.push(stepData);
        }

        console.log(`[Game Interface] Data list created with ${dataList.length} items`);
        console.log(`[Game Interface] Sample data:`, dataList[0]);

        setPoints(newPoints ? newPoints : availablePoints); // Allocate points.

        console.log(`[Game Interface] About to emit data to WebSocket...`);
        console.log(`[Game Interface] Socket exists: ${socket ? 'yes' : 'no'}`);
        
        socket?.emitData(dataList); // <-- Sends data to socket client.
        
        console.log(`[Game Interface] Data emitted to WebSocket, setting cursor to wait`);
        document.body.style.cursor = "wait";
    }


    function finishRound() {
        setData(targetData);
        onEndRound();
    }

    function handleOpenTopPanel() {
        setTopPanel({...topPanel, open: !topPanel.open});
    }


    const mobile = useMediaQuery('(max-width:600px)');
    const desktop = useMediaQuery('(min-width:601px)');

   



    return (
        <Container>
            <MainWindow>
                <TopPanel className="wrapper-top-panel">

                    <Box className="container-top">
                        <Box className="container-top-left" data-totla={!!mobile && !topPanel.open ? 2 : 5}>
                            <CurrentTime hours={time.hours} minutes={time.minutes} />

                            <AdvanceTime time={time} targetTime={targetTime} setTargetTime={setTargetTime}
                            onEndRound={finishRound} triggerAction={triggerAction} canTrigger={canTrigger} />

                            { (!!topPanel.open || !!desktop) && <Weather  weather={weatherPrevisions} />}
                            { (!!topPanel.open || !!desktop) && <ConsultMeteo weather={weatherPrevisions} />}
                            { (!!topPanel.open || !!desktop) && <Room n={nPeople[round][currentStep]} />}

                            {!!mobile && <Box onClick={handleOpenTopPanel} className="mobile">{topPanel.open ? <IconArrowUp /> : <IconArrowDown />}</Box>}

                            {/*
                            <Tasks time={time} points={availablePoints} triggerAction={triggerTask} canTrigger={canTrigger}
                            tasks={tasks} setTasks={setTasks} tasksInProgress={tasksInProgress} />
                            */}
                            
                        </Box> 
                        <Box className="container-top-right">
                            
                            { (!!topPanel.open || !!desktop) && <>
                            <Box className="box-result box-un"> 
                                <h3 className="font-list no-border">Consommation totale en énergie</h3>
                                <Typography>{(values.energie_tot || 0).toFixed(0) + " kWh"}</Typography>
                            </Box>
                            </>}
                            { (!!topPanel.open || !!desktop) && <>
                                <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 1, borderColor: '#fff', margin: '0 5px' }} />
                                <Box className="box-result box-deux"> 
                                    <h3 className="font-list no-border">Coût</h3> 
                                    <Typography>{(values.cost || 0).toFixed(2) + "$"}</Typography>
                                </Box>
                            

                            <Divider orientation="vertical" flexItem sx={{ borderRightWidth: 1, borderColor: '#fff', margin: '0 5px' }} />
                            </>}
                            <Box className="box-result pointage"> 
                                <h3 className="font-list no-border">Pointage</h3> 
                                {(scores.length > 0 && scores[round].length > 0) ? <CurrentScore points={scores[round][scores[round].length - 1].score} /> : null}
                            </Box> 
                            




                            {!!mobile && <Box onClick={handleOpenTopPanel} className="mobile">{topPanel.open ? <IconArrowUp /> : <IconArrowDown />}</Box>}

                        </Box> 

                    </Box>
                    <Box className="wrapper-info-events">
                        <Events scrollRef={eventsScroller} points={availablePoints} time={time} triggerAction={triggerTask} canTrigger={canTrigger} tasksInProgress={tasksInProgress} /> 
                        <Box className="container-info-events">
                            <InfoIconCarre />
                            <Typography className="info-events">Avancez l’heure lorsque vous avez terminé</Typography>
                        </Box>
                    </Box>
                </TopPanel>

                <Controller data={data} targetData={targetData} setTargetData={setTargetData}
                    availablePoints={availablePoints} changeAvailablePoints={setAvailablePoints} values={values} />
                
            </MainWindow>


        </Container>

        

    );
};


const Container = styled.div`
    display: flex;
    flex-direction: row;
    width: 100vw;
`;

const MainWindow = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 6;
`;

const TopPanel = styled.div`
    display: flex;
    margin: 0em .5em;
    & > * {
        margin: 0em .5em;
    }
`;


export default GameInterface;

{/*
        <Container>
            <MainWindow>
                <TopPanel>
                    <CurrentTime hours={time.hours} minutes={time.minutes} />
                    <Room n={nPeople[round][currentStep]} />
                    <Budget points={availablePoints} tasksInProgress={tasksInProgress} setPoints={setAvailablePoints} maxAvailablePoints={points}
                        data={data} targetData={targetData} setTargetData={setTargetData} setTasksInProgress={setTasksInProgress} />
                    {(scores.length > 0 && scores[round].length > 0) ? <CurrentScore points={scores[round][scores[round].length - 1].score} /> : null}
                    <Round round={round + 1} />
                </TopPanel>
                <Controller data={data} targetData={targetData} setTargetData={setTargetData}
                    availablePoints={availablePoints} changeAvailablePoints={setAvailablePoints} values={values} />
            </MainWindow>

            <Stack width={300} sx={{ pr: 3, maxHeight: "95vh" }} gap={2}>
                <Weather weather={weatherPrevisions} />
                <Events scrollRef={eventsScroller} points={availablePoints} time={time} triggerAction={triggerTask} canTrigger={canTrigger} tasksInProgress={tasksInProgress} />
                <Tasks time={time} points={availablePoints} triggerAction={triggerTask} canTrigger={canTrigger}
                    tasks={tasks} setTasks={setTasks} tasksInProgress={tasksInProgress} />
                <AdvanceTime time={time} targetTime={targetTime} setTargetTime={setTargetTime}
                    onEndRound={finishRound} triggerAction={triggerAction} canTrigger={canTrigger} />
            </Stack>

            {targetEvent && <Popup
                isShown={showEventPopup}
                hide={toggleEventPopup}
                headerText={targetEvent.name}
                modalContent={<SpontaneousEventPopup hide={toggleEventPopup} event={targetEvent} eventId={targetEventId} time={time} points={0} triggerAction={triggerTask} tasksInProgress={tasksInProgress} />}
                disableButton={true}
            />}

        </Container>  
*/}